import type { Attributes, Graph } from './graph.d.ts';
/**
 * @property format
 * The {@link https://www.graphviz.org/docs/outputs/ | Graphviz output format} to render. For example, `"dot"` or `"svg"`.
 *
 * @property engine
 * The {@link https://www.graphviz.org/docs/layouts/ | Graphviz layout engine} to use for graph layout. For example, `"dot"` or `"neato"`.
 *
 * @property yInvert
 * Invert y coordinates in output. This corresponds to the {@link https://www.graphviz.org/doc/info/command.html#-y | `-y`} Graphviz command-line option</a>.
 *
 * @property reduce
 * Reduce the graph. This corresponds to the {@link https://www.graphviz.org/doc/info/command.html#-x | `-x`} Graphviz command-line option</a>.
 *
 * @property graphAttributes
 * Sets the default graph attributes. This corresponds the {@link https://www.graphviz.org/doc/info/command.html#-G | `-G`} Graphviz command-line option</a>.
 *
 * @property nodeAttributes
 * Sets the default node attributes. This corresponds the {@link https://www.graphviz.org/doc/info/command.html#-N `-N`} Graphviz command-line option</a>.
 *
 * @property edgeAttributes
 * Sets the default edge attributes. This corresponds the {@link https://www.graphviz.org/doc/info/command.html#-E | `-E`} Graphviz command-line option</a>.
 *
 * @property images
 * The name of the image used as a key of the object.
 * In addition to filenames, names that look like absolute filesystem paths or URLs can be used.
 * For example:
 *
 * - `"example.png"`
 * - `"/images/example.png"`
 * - `"http://example.com/image.png"`
 *
 * Names that look like relative filesystem paths, such as `"../example.png"`, are not supported.
 * Image sizes to use when rendering nodes with <code>image</code> attributes.
 *
 * For example, to indicate to Graphviz that the image <code>test.png</code> has size 300x200:
 *
 * ```js
 * viz.render('graph { a[image="test.png"] }', {
 *   images: {
 *     'test.png': { width: 300, height: 200 },
 *   },
 * });
 * ```
 */
export interface RenderOptions {
  format?: string;
  engine?: string;
  yInvert?: boolean;
  reduce?: boolean;
  graphAttributes?: Attributes;
  nodeAttributes?: Attributes;
  edgeAttributes?: Attributes;
  images?: Record<string, ImageSize>;
  svgBasePath?: string;
}

/**
 * The result object returned by {@link Viz.render}.
 */
export type RenderResult = SuccessResult | FailureResult;

/**
 * Returned by {@link Viz.render} if rendering was successful. `errors` may contain warning messages even if the graph rendered successfully.
 */
export interface SuccessResult {
  status: 'success';
  output: string;
  errors: RenderError[];
}

/**
 * Returned by {@link Viz.render} or {@link Viz.renderFormats} if rendering failed.
 */
export interface FailureResult {
  status: 'failure';
  output: null;
  errors: RenderError[];
}

/**
 * The result object returned by {@link Viz.renderFormats}.
 */
export type MultipleRenderResult = MultipleSuccessResult | FailureResult;

/**
 * Returned by {@link Viz.renderFormats} if rendering was successful. `errors` may contain warning messages even if the graph rendered successfully.
 */
export interface MultipleSuccessResult {
  status: 'success';
  output: Record<string, string>;
  errors: RenderError[];
  unresolved_images: string[];
}

export interface RenderError {
  level: 'error' | 'warning' | undefined;
  message: string;
}

/**
 * Specifies the size of an image used as a node's `image` attribute. See {@link RenderOptions.images}.
 *
 * `width` and `height` may be specified as numbers or strings with units: in, px, pc, pt, cm, or mm. If no units are given or measurements are given as numbers, points (pt) are used.
 *
 * @property width
 * The width of the image.
 *
 * @property height
 * The height of the image.
 */
export interface ImageSize {
  width: string | number;
  height: string | number;
}

/**
 * The {@link Viz} class isn't exported, but it can be instantiated using the {@link instance} function.
 */
export class Viz {
  _stdoutBuf = '';
  _stderrBuf = '';
  _utf8Encoder: TextEncoder = new TextEncoder();
  _utf8Decoder: TextDecoder = new TextDecoder('utf8');
  _unresolved_images: string[] = [];
  _wasm: {
    memory: Uint8Array;
    wasm_alloc(length: number): number;
    wasm_free(ptr: number, length: number): void;
    render(jsonPtr: number, jsonLength: number): bigint;
  };
  _canvas_ctx: OffscreenCanvasRenderingContext2D;

  /**
   * @internal
   */
  constructor(instance: WebAssembly.Instance) {
    // @ts-expect-error not sure how to properly type it
    this._wasm = instance.exports;
    const canvas = new OffscreenCanvas(0, 0);
    const canvas_ctx = canvas.getContext('2d');
    if (canvas_ctx == null) {
      throw new Error('canvas context is null');
    }
    this._canvas_ctx = canvas_ctx;
  }

  /**
   * Renders the graph described by the input for each format in `formats` and returns the result as an object. For a successful result, `output` is an object keyed by format.
   */
  renderFormats(
    input: string | Graph,
    formats: readonly string[],
    options: RenderOptions = {},
  ): MultipleRenderResult {
    return this._renderInput(input, formats, {
      engine: 'dot',
      ...options,
    });
  }

  /**
   * Renders the graph described by the input and returns the result as an object.
   *
   * `input` may be a string in {@link https://www.graphviz.org/doc/info/lang.html | DOT syntax} or a {@link Graph | graph object}.
   *
   * This method does not throw an error if rendering failed, including for invalid DOT syntax, but it will throw for invalid types in input or unexpected runtime errors.
   */
  render(input: string | Graph, options: RenderOptions = {}): RenderResult {
    const format = options.format ?? 'dot';

    const result = this._renderInput(input, [format], {
      engine: 'dot',
      ...options,
    });

    return result.status === 'success'
      ? {
          ...result,
          output: result.output[format],
        }
      : result;
  }

  /**
   * Renders the input and returns the result as a string. Throws an error if rendering failed.
   */
  renderString(input: string | Graph, options: RenderOptions = {}): string {
    const result = this.render(input, options);

    if (result.status !== 'success') {
      throw new Error(
        result.errors.find((e) => e.level == 'error')?.message ??
          'render failed',
      );
    }

    return result.output;
  }

  _renderInput(
    input: string | Graph,
    formats: readonly string[],
    options: RenderOptions,
  ): MultipleRenderResult {
    let renderGv = false;
    let renderDot = false;
    let renderSvg = false;
    for (const name of formats) {
      switch (name) {
        case 'gv': {
          renderGv = true;
          break;
        }
        case 'dot': {
          renderDot = true;
          break;
        }
        case 'svg': {
          renderSvg = true;
          break;
        }
        default: {
          return {
            status: 'failure',
            output: null,
            errors: [
              {
                level: 'error',
                message:
                  'Format: "invalid" not recognized. Use one of: dot gv svg',
              },
            ],
          };
        }
      }
    }
    const requestJSON = JSON.stringify(
      {
        graph: typeof input === 'string' ? { dot: input } : { graph: input },
        graphAttributes: options.graphAttributes ?? null,
        nodeAttributes: options.nodeAttributes ?? null,
        edgeAttributes: options.edgeAttributes ?? null,
        renderDot: renderDot || renderGv,
        renderSvg,
        engine: options.engine ?? 'dot',
        yInvert: options.yInvert ?? false,
        reduce: options.reduce ?? false,
        images: this._normalizeImages(options.images),
        svgBasePath: options.svgBasePath,
      },
      null,
      2,
    );
    const cJson = this._utf8Encoder.encode(requestJSON);
    const jsonPtr = this._wasm.wasm_alloc(cJson.length);
    const inputJSONBuf = new Uint8Array(
      this._wasm.memory.buffer,
      jsonPtr,
      cJson.length,
    );
    inputJSONBuf.set(cJson);
    this._unresolved_images = [];
    const sliceU64 = this._wasm.render(
      inputJSONBuf.byteOffset,
      inputJSONBuf.length,
    );
    const ptr = Number(BigInt.asUintN(32, sliceU64));
    const len = Number(BigInt.asUintN(32, sliceU64 >> 32n));
    const outputJSONBuf = new Uint8Array(this._wasm.memory.buffer, ptr, len);
    try {
      const str = this._decodeWasmString(sliceU64);
      const response = JSON.parse(str) as MultipleRenderResult;
      let output: Record<string, string> | null = null;
      if (response.output) {
        response.unresolved_images = this._unresolved_images;
        output = {};
        if (renderGv) {
          output.gv = response.output.dot;
        }
        if (renderDot) {
          output.dot = response.output.dot;
        }
        if (renderSvg) {
          output.svg = response.output.svg;
        }
      }
      response.output = output;
      return response;
    } finally {
      this._wasm.wasm_free(outputJSONBuf.byteOffset, outputJSONBuf.length);
    }
  }

  _normalizeImages(
    images: Record<string, ImageSize> = {},
  ): Record<string, ImageSize> {
    return Object.fromEntries(
      Object.entries(images).map(([name, { height, width }]) => [
        name,
        { height: height.toString(), width: width.toString() },
      ]),
    );
  }

  _wasi_fd_write(
    fd: number,
    iovs_ptr: number,
    iovs_len: number,
    nwritten_ptr: number,
  ): number {
    const mem = new Uint8Array(this._wasm.memory.buffer);
    const view = new DataView(this._wasm.memory.buffer);

    let totalWritten = 0;

    let bufferStr = '';
    for (let i = 0; i < iovs_len; i++) {
      const base = view.getUint32(iovs_ptr + i * 8, true);
      const len = view.getUint32(iovs_ptr + i * 8 + 4, true);
      const chunk = mem.subarray(base, base + len);
      bufferStr += this._utf8Decoder.decode(chunk);

      totalWritten += len;
    }

    switch (fd) {
      case 1: {
        const lines = (this._stdoutBuf + bufferStr).split('\n');
        this._stdoutBuf = lines.pop() ?? '';
        for (const line of lines) {
          console.log(line);
        }
        break;
      }
      case 2: {
        const lines = (this._stderrBuf + bufferStr).split('\n');
        this._stderrBuf = lines.pop() ?? '';
        for (const line of lines) {
          console.error(line);
        }
        break;
      }
      default: {
        console.trace(`fd_write: unknown fd ${fd.toString()}`);
        return 52; // WASI_ERRNO_NOTSUP
      }
    }

    view.setUint32(nwritten_ptr, totalWritten, true);
    return 0;
  }

  _canvas_measure_text(
    font_name_slice: bigint,
    text_slice: bigint,
    bold: boolean,
    italic: boolean,
  ): number {
    const fontName = this._decodeWasmString(font_name_slice);
    const text = this._decodeWasmString(text_slice);

    const pxPerPt = 96 / 72;

    const sizePt = 16;
    const sizePx = sizePt * pxPerPt;

    const weight = bold ? 'bold ' : '';
    const style = italic ? 'italic ' : '';
    const family =
      fontName && fontName.trim().length > 0 ? fontName : 'sans-serif';

    const cssFont = `${style}${weight}${sizePx.toString()}px "${family}"`;
    this._canvas_ctx.font = cssFont;

    const metrics = this._canvas_ctx.measureText(text);
    const widthPx = metrics.width;

    const widthPt = widthPx / pxPerPt / sizePt;
    return widthPt;
  }

  _js_image_get_dimensions(path: bigint) {
    const pathStr = this._decodeWasmString(path);
    this._unresolved_images.push(pathStr);
    // const url = 'https://www.graphviz.org/Gallery/directed/' + pathStr;
    // const xhr = new XMLHttpRequest();
    // xhr.open('GET', url, false);
    // xhr.send(null);
    // const bytes = new TextEncoder().encode(xhr.response);

    // if (xhr.status === 200) {
    //   // Manually convert the raw string (potentially binary) to a base-64 encoded string.
    //   // This is error-prone for non-text data if the encoding is wrong.
    //   const rawData = xhr.responseText;
    //   const base64 = btoa(rawData);

    //   // You need to manually determine the MIME type of the content.
    //   // A better way might be to get it from the Content-Type header if the server sends it.
    //   const contentType =
    //     xhr.getResponseHeader('Content-Type') ?? 'application/octet-stream';

    //   const dataurl = `data:${contentType};base64,${base64}`;
    //   const image = new Image();
    //   image.src = dataurl;
    //   return (BigInt(image.height) << 32n) | BigInt(image.width);
    // } else {
    //   console.error('Error fetching data:', xhr.statusText);
    //   return 0n;
    // }
  }

  _decodeWasmString(sliceU64: bigint): string {
    const ptr = Number(BigInt.asUintN(32, sliceU64));
    const len = Number(BigInt.asUintN(32, sliceU64 >> 32n));
    const outputJSONBuf = new Uint8Array(this._wasm.memory.buffer, ptr, len);
    return this._utf8Decoder.decode(outputJSONBuf);
  }
}
